import type { AffiliationType } from '../types/registration'

export const WORDMARK = 'Stepping Stone'

export const AFFILIATION_OPTIONS: { label: string; value: AffiliationType }[] = [
  { label: 'I was in a program here', value: 'alumni' },
  { label: 'I worked here', value: 'staff' },
  { label: 'I volunteered here', value: 'volunteer' },
  { label: 'I served on the board', value: 'board_member' },
  { label: 'I supported the work', value: 'supporter' },
  { label: 'I’m family or a friend of someone who was', value: 'family' },
]

export const PROGRAM_TYPES = [
  'Residential',
  'Outpatient',
  'Aftercare group',
  'Housing support',
  'Job readiness',
  'Mentorship',
  'Community meals',
]

export const ATTRIBUTION_OPTIONS = [
  { label: 'First name and last initial', value: 'initials' },
  { label: 'My full first and last name', value: 'name' },
  { label: 'No name at all', value: 'anonymous' },
]

const FIRST_YEAR = 1976
const CURRENT_YEAR = 2026

export const YEAR_OPTIONS = Array.from(
  { length: CURRENT_YEAR - FIRST_YEAR + 1 },
  (_, index) => {
    const year = String(CURRENT_YEAR - index)
    return { label: year, value: year }
  },
)

/** Aggregate community figures. Never per-person, never rankable. */
export const COMMUNITY = {
  members: 129,
  decadesRepresented: 6,
  collectiveYears: '1,850',
}

/** Recently arrived members, shown as first name + last initial only. */
export const RECENT_ARRIVALS: Record<string, string[]> = {
  '1970s': ['Marion A.', 'Ken D.'],
  '1980s': ['Dev R.', 'Paulette S.', 'Ray M.'],
  '1990s': ['Theresa M.', 'Andre P.', 'Lorna B.'],
  '2000s': ['Danielle W.', 'Owen T.', 'Priya N.'],
  '2010s': ['Nadia K.', 'Marcus J.', 'Elena V.'],
  '2020s': ['Sam O.', 'Iris L.'],
}

/** How many members are already here from each decade. */
export const DECADE_MEMBERS: Record<string, number> = {
  '1970s': 4,
  '1980s': 11,
  '1990s': 23,
  '2000s': 31,
  '2010s': 42,
  '2020s': 18,
}

export const PANEL_PHOTOS = {
  building: {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/9c79d4dd-6ea3-440a-a0ac-61286d2b42b8.jpg',
    alt: 'The original storefront building on a quiet street, photographed on an overcast winter day',
  },
  room: {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/f1ca7623-c2af-47eb-bd49-c1269a63bfc0.jpg',
    alt: 'An empty meeting room with a loose circle of folding chairs and a coffee urn',
  },
  hands: {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/0e0369c5-6708-46ac-9d22-b28ecc21e6a4.jpg',
    alt: 'Two mugs of coffee and a notebook on a scuffed wooden table, an older person’s hands resting beside them',
  },
  founding: {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/840120e7-3eef-4515-884d-5ba1c0e7df9c.jpg',
    alt: 'People of different ages around folding tables in the original basement drop-in room, late 1970s',
  },
  era: {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/312d6926-fd9d-4005-8c13-615769a4d385.jpg',
    alt: 'A worn storefront meeting room with mismatched chairs and handmade posters',
  },
  voices: {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/5596988d-7e05-47d1-8fe2-41663c9e8b24.jpg',
    alt: 'A longtime member sitting by a window, mid-memory',
  },
}

interface EraNote {
  tag: string
  headline: string
  body: string
}

export const ERA_STORIES: Record<string, EraNote> = {
  '1970s': {
    tag: 'The 1970s',
    headline: 'Two folding tables and a kettle.',
    body: 'The first winter, meetings ran three nights a week in a borrowed basement. Nobody had a title.',
  },
  '1980s': {
    tag: 'The 1980s',
    headline: 'The Oak Street years.',
    body: 'The basement gave way to a storefront, and the first aftercare groups met in the back room.',
  },
  '1990s': {
    tag: 'The 1990s',
    headline: 'Three doors, one block.',
    body: 'Residential, outpatient, and housing support ran out of three buildings within four blocks.',
  },
  '2000s': {
    tag: 'The 2000s',
    headline: 'People started coming back.',
    body: 'The first generation of alumni returned as mentors — the same rooms, the other side of the table.',
  },
  '2010s': {
    tag: 'The 2010s',
    headline: 'Beyond the block.',
    body: 'Two more sites opened and family support grew from a side room into a program of its own.',
  },
  '2020s': {
    tag: 'The 2020s',
    headline: 'Meeting people where they are.',
    body: 'Remote groups, community meals, and a housing team that worked out of a parking lot for a year.',
  },
}

export const ERA_QUOTES: Record<string, EraNote> = {
  '1970s': {
    tag: 'In their words',
    headline: '“Somebody left the light on for us.”',
    body: 'Marion A., here in 1979.',
  },
  '1980s': {
    tag: 'In their words',
    headline: '“The back room on Oak Street saved my life twice.”',
    body: 'Dev R., here 1984–1989.',
  },
  '1990s': {
    tag: 'In their words',
    headline: '“I learned to ask for help in that building.”',
    body: 'Theresa M., here 1996.',
  },
  '2000s': {
    tag: 'In their words',
    headline: '“I came back because someone did it for me.”',
    body: 'Andre P., here since 2004.',
  },
  '2010s': {
    tag: 'In their words',
    headline: '“It was the one place nobody rushed me.”',
    body: 'Nadia K., here 2013–2016.',
  },
  '2020s': {
    tag: 'In their words',
    headline: '“We cooked for four hundred people in a parking lot.”',
    body: 'Sam O., here since 2021.',
  },
}

export function decadeFor(year: string): string {
  const parsed = Number(year)
  if (!parsed || Number.isNaN(parsed)) return '1990s'
  const decade = Math.floor(parsed / 10) * 10
  if (decade <= 1970) return '1970s'
  if (decade >= 2020) return '2020s'
  return `${decade}s`
}

export function membersFromDecade(year: string): number {
  return DECADE_MEMBERS[decadeFor(year)] ?? 0
}
